import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'zui-preferences';

const subscribe = (callback: () => void) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

const readAll = (): Record<string, unknown> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    // Reject anything that isn't a plain object — e.g. the string `"null"` parses
    // to JS `null`, `"42"` parses to a number, `"[1,2,3]"` parses to an array.
    // Returning any of these would let callers index into a non-record and throw.
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as Record<string, unknown>;
  } catch {
    return {};
  }
};

const writeKey = (key: string, value: unknown): void => {
  const next = { ...readAll(), [key]: value };
  try {
    const serialized = JSON.stringify(next);
    window.localStorage.setItem(STORAGE_KEY, serialized);
    // localStorage only fires `storage` events across tabs; dispatch synthetically so
    // same-tab `useSyncExternalStore` subscribers observe the change too.
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY, newValue: serialized }));
  } catch {
    /* pass — fall back to not persisting rather than crashing the caller */
  }
};

/**
 * Reads a single boolean key from the `zui-preferences` localStorage entry and
 * re-renders only when that key's value changes.
 */
export const useUiFlag = (
  key: string,
  defaultValue: boolean,
): [boolean, (next: boolean) => void] => {
  const getSnapshot = useCallback((): boolean => {
    const value = readAll()[key];
    return typeof value === 'boolean' ? value : defaultValue;
  }, [key, defaultValue]);

  const value = useSyncExternalStore(subscribe, getSnapshot, () => defaultValue);

  const setValue = useCallback((next: boolean) => writeKey(key, next), [key]);

  return [value, setValue];
};

/**
 * Reads a single string key from the `zui-preferences` localStorage entry and
 * re-renders only when that key's value changes.
 */
export const useUiPreference = (
  key: string,
  defaultValue: string,
): [string, (next: string) => void] => {
  const getSnapshot = useCallback((): string => {
    const value = readAll()[key];
    return typeof value === 'string' ? value : defaultValue;
  }, [key, defaultValue]);

  const value = useSyncExternalStore(subscribe, getSnapshot, () => defaultValue);

  const setValue = useCallback((next: string) => writeKey(key, next), [key]);

  return [value, setValue];
};
